import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../SvgIcon";

const getFrequencyChecks = (t) => [
  { displayText: t("questionnaireSchedule.weekly"), value: "weekly" },
  { displayText: t("questionnaireSchedule.biWeekly"), value: "biweekly" },
  { displayText: t("questionnaireSchedule.monthly"), value: "monthly" },
];

const buildSchedule = ({
  currentFrequency,
  currentWeekDay,
  startCheckInDay,
  selectedWeeklyDays,
}) => ({
  dayOfWeek: currentWeekDay,
  startDay: startCheckInDay
    ? Number(moment(startCheckInDay).format("YYYYMMDD"))
    : 0,
  frequency: currentFrequency,
  selectedWeeklyDays,
});

const QuestionnaireScheduleFields = ({
  startingCalendarDay,
  initialSchedule,
  onChange,
}) => {
  const { t } = useTranslation("coaching-portal");
  const frequencyChecks = getFrequencyChecks(t);

  const defaultWeekdays = Array.apply(null, Array(7)).map((_, i) =>
    moment(i, "e")
      .startOf("week")
      .isoWeekday(i + startingCalendarDay)
      .format("dddd")
      .slice(0, 3)
      .toLowerCase()
  );

  const initSelectedWeeklyDays = defaultWeekdays.map((day) => ({
    day,
    active: false,
  }));

  const [currentFrequency, setCurrentFrequency] = useState(
    initialSchedule ? initialSchedule.frequency : ""
  );
  const [currentWeekDay, setCurrentWeekDay] = useState(
    initialSchedule ? initialSchedule.dayOfWeek : ""
  );
  const [selectedWeeklyDays, setSelectedWeeklyDays] = useState(
    initialSchedule?.selectedWeeklyDays
      ? initialSchedule.selectedWeeklyDays
      : initSelectedWeeklyDays
  );
  const [startCheckInDay, setStartCheckInDay] = useState(
    initialSchedule ? moment(String(initialSchedule.startDay)) : null
  );

  const notify = (patch) => {
    const next = buildSchedule({
      currentFrequency,
      currentWeekDay,
      startCheckInDay,
      selectedWeeklyDays,
      ...patch,
    });
    onChange(next);
  };

  const handleCheckInDay = (index, patchState) => {
    const today = moment();
    const dayOfTheWeek = moment()
      .startOf("week")
      .add(startingCalendarDay + index, "days");

    const nextStartDay = today.isSameOrAfter(dayOfTheWeek)
      ? moment(dayOfTheWeek).add(7, "days")
      : moment(dayOfTheWeek);

    setStartCheckInDay(nextStartDay);
    notify({ startCheckInDay: nextStartDay, ...patchState });
  };

  const handleFrequencyClick = (value) => {
    setCurrentFrequency(value);
    notify({ currentFrequency: value });
  };

  const handleWeekDayClick = (value, index) => {
    setCurrentWeekDay(value);
    handleCheckInDay(index, { currentWeekDay: value });
  };

  const handleWeeklyDayClick = (value, index) => {
    const next = selectedWeeklyDays.map((obj) =>
      obj.day === value ? { day: value, active: !obj.active } : obj
    );
    setSelectedWeeklyDays(next);
    handleCheckInDay(index, { selectedWeeklyDays: next });
  };

  return (
    <>
      <section className="schedule-frequency">
        <h5>{t("questionnaireSchedule.frequencyLabel")}</h5>
        {frequencyChecks.map((frequency) => (
          <div
            className="frequency-item"
            key={frequency.value}
            onClick={() => handleFrequencyClick(frequency.value)}
          >
            <SvgIcon
              src={
                currentFrequency === frequency.value
                  ? "/img/new-icons/tick-circle.svg"
                  : "/img/new-icons/radio-unchecked.svg"
              }
              noFilterOnLight={currentFrequency === frequency.value}
            />
            <p className="p5">{frequency.displayText}</p>
          </div>
        ))}
      </section>
      <section className="schedule-weekday">
        <h5>
          {currentFrequency === "daily"
            ? t("questionnaireSchedule.selectDaysOfWeek")
            : t("questionnaireSchedule.selectDayOfWeek")}
        </h5>
        <div className="schedule-weekdays">
          {defaultWeekdays.map((weekDay, index) => (
            <div
              key={weekDay}
              className={
                currentWeekDay === weekDay
                  ? "p4 accountability-day active"
                  : "p4 accountability-day"
              }
              onClick={() =>
                currentFrequency === "daily"
                  ? handleWeeklyDayClick(weekDay, index)
                  : handleWeekDayClick(weekDay, index)
              }
            >
              {weekDay.slice(0, 1).toUpperCase()}
            </div>
          ))}
        </div>
      </section>
      {startCheckInDay && (
        <h5>
          {t("questionnaireSchedule.firstReportDate", {
            date: startCheckInDay.format("MMM D, YYYY"),
          })}
        </h5>
      )}
    </>
  );
};

export default QuestionnaireScheduleFields;
